/* ═══════════════════════════════════════════════════════════════
 * privacyThemeTokens.ts
 *
 * Token registry for the Privacy Suite design system.
 *
 * The Privacy Suite (Zcash + Monero wizards, send/receive flows,
 * activity inspectors) opts into a SHARED design language so the
 * surface stays cohesive across coins. Each theme exposes the same
 * named tokens; the CSS for the suite is written against those
 * tokens via CSS custom properties, so switching themes is a
 * single attribute flip on the document root — no per-component
 * conditional rendering required.
 *
 * Themes:
 *
 *   • `classic`  – the historic Win 3.1 grey chrome the rest of
 *                  WINBIT32 ships with. Defaults match the
 *                  literal hex values that were previously
 *                  hard-coded in `MoneroPanel.css`,
 *                  `ZcashTransferWizard.css`, etc.
 *
 *   • `phosphor` – a 90s-flavoured Metro/Encarta-influenced dark
 *                  theme: true black surfaces with amber phosphor
 *                  accents. Designed to *feel* private — minimal
 *                  chrome, no distracting colour, soft Aero-ish
 *                  glow on focus instead of harsh outlines.
 *
 * The token shape is exhaustive on purpose: every colour, border
 * style and glow used by the Privacy Suite has a slot here, so the
 * downstream CSS never has to reach for a literal hex value.
 * That means new themes only need to fill in the same fields to
 * be safe to drop into the menu.
 *
 * The CSS variable names follow the pattern `--privacy-{group}-
 * {role}` so they don't collide with the global Win 3.1 chrome
 * variables shared across the desktop.
 * ═══════════════════════════════════════════════════════════════ */

/**
 * Stable identifiers for the bundled themes. Adding a new theme
 * means adding to this union, exporting a new tokens object below,
 * and registering it in `PRIVACY_THEME_REGISTRY`.
 */
export type PrivacyThemeId = 'classic' | 'phosphor';

/**
 * Token shape — every theme MUST populate all of these slots.
 * The fields are grouped to match the surfaces in the suite.
 */
export interface PrivacyThemeTokens {
	/** Display name shown in the menu (e.g. "Classic", "Phosphor"). */
	readonly label: string;
	/** One-line description shown in the menu / about dialog. */
	readonly description: string;
	/** Stable ID — must match the registry key. */
	readonly id: PrivacyThemeId;
	/** Page background + surfaces. */
	readonly surface: {
		/** Outermost background — the window content area. */
		readonly base: string;
		/** Cards, panels, raised tiles. */
		readonly raised: string;
		/** Subtly highlighted band — input wells, list rows. */
		readonly sunken: string;
		/** Hovered tile / button background. */
		readonly hover: string;
		/** Pressed / active tile background. */
		readonly active: string;
	};
	/** Foreground / text. */
	readonly text: {
		readonly primary: string;
		readonly secondary: string;
		readonly muted: string;
		/** Inverted text — used on accent-coloured fills. */
		readonly inverse: string;
	};
	/** Accents — the "amber" in phosphor, the "Win blue" in classic. */
	readonly accent: {
		/** The main accent — links, highlighted labels. */
		readonly primary: string;
		/** Stronger accent for focus rings + emphasis. */
		readonly focus: string;
		/** A dimmer accent for muted highlights. */
		readonly dim: string;
		/** Soft halo / glow colour with alpha. */
		readonly glow: string;
	};
	/** Status colours — used for outcome rows, error banners. */
	readonly status: {
		readonly success: string;
		readonly warning: string;
		readonly danger: string;
		readonly info: string;
	};
	/** Borders / dividers. Classic uses outset/inset bevels;
	 * phosphor uses a single-tone amber stroke. */
	readonly border: {
		/** Top + left edges of a raised bevel. */
		readonly outsetLight: string;
		/** Bottom + right edges of a raised bevel. */
		readonly outsetDark: string;
		/** Top + left edges of a sunken (inset) bevel. */
		readonly insetLight: string;
		/** Bottom + right edges of a sunken (inset) bevel. */
		readonly insetDark: string;
		/** A flat 1px divider — e.g. between groups. */
		readonly divider: string;
	};
	/** Typography hints — themes can prefer different fonts. */
	readonly typography: {
		readonly heading: string;
		readonly body: string;
		readonly mono: string;
	};
	/** Optional decorative effects. */
	readonly effects: {
		/** Box-shadow used for focus / "scanning" pulses. */
		readonly focusRing: string;
		/** Box-shadow used for hovered tiles. */
		readonly tileHover: string;
	};
}

/* ──────────────────────────────────────────────────────────────
 * Theme: classic
 *
 * Mirrors the colours that lived as hard-coded hex values in the
 * pre-theme CSS (`MoneroPanel.css`, `ZcashApp.css`,
 * `ZcashTransferWizard.css`). The tokens are intentionally
 * non-identical to the global Win 3.1 chrome so we can tweak the
 * Privacy Suite independently of the desktop without touching
 * `win3.css`.
 * ────────────────────────────────────────────────────────────── */
export const CLASSIC_THEME: PrivacyThemeTokens = {
	id: 'classic',
	label: 'Classic',
	description: '90s grey Win 3.1 / Win 95 chrome — same as the rest of WINBIT32.',
	surface: {
		base: '#c0c0c0',
		raised: '#efefef',
		sunken: '#e0e0e0',
		hover: '#d4d4d4',
		active: '#bfbfbf',
	},
	text: {
		primary: '#000000',
		secondary: '#404040',
		muted: '#606060',
		inverse: '#ffffff',
	},
	accent: {
		primary: '#000080',
		focus: '#3030c0',
		dim: '#4040a0',
		glow: 'rgba(0, 0, 128, 0.35)',
	},
	status: {
		success: '#006000',
		warning: '#a06000',
		danger: '#a00000',
		info: '#000080',
	},
	border: {
		outsetLight: '#ffffff',
		outsetDark: '#404040',
		insetLight: '#808080',
		insetDark: '#ffffff',
		divider: '#808080',
	},
	typography: {
		heading: "'System', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
		body: "'MS Sans Serif', 'System', Tahoma, sans-serif",
		mono: "'Courier New', monospace",
	},
	effects: {
		focusRing: '0 0 0 1px #000080',
		tileHover: 'inset 0 0 0 1px #808080',
	},
};

/* ──────────────────────────────────────────────────────────────
 * Theme: phosphor
 *
 * A 90s-flavoured Metro/Encarta-style dark surface with amber
 * phosphor accents. The choice of amber over green is a nod to
 * the Tek storage tube monitors of the late 80s; it reads as
 * "diagnostic / private" rather than "Matrix prop". The values
 * follow:
 *
 *   • Background           — true black, deep enough to look
 *                            screen-off in dim rooms.
 *   • Raised surface       — barely-warm black (#0a0700) so cards
 *                            still feel tactile under the amber.
 *   • Primary accent       — #ffb000 (Tek 4014 amber). The same
 *                            hue is used for body text so the
 *                            entire surface reads "phosphor on
 *                            black" without further effort.
 *   • Focus accent         — #ffd66b, a softer pastel amber so
 *                            focus rings don't strain.
 *   • Status               — amber-shifted variants of the four
 *                            classic states; greens shift to
 *                            warm-amber, reds stay red (you must
 *                            still notice errors).
 *
 * The Aero-influence is the focus halo: a 0.35-alpha amber glow
 * around focusable surfaces instead of a hard outline. That keeps
 * the screen calm while still telegraphing what's interactive.
 * ────────────────────────────────────────────────────────────── */
export const PHOSPHOR_THEME: PrivacyThemeTokens = {
	id: 'phosphor',
	label: 'Phosphor',
	description:
		'Encarta-style dark surface with amber phosphor accents. Subdued, private-feeling.',
	surface: {
		base: '#000000',
		raised: '#0a0700',
		sunken: '#050300',
		hover: '#1a1300',
		active: '#231900',
	},
	text: {
		primary: '#ffb000',
		secondary: '#cc8a00',
		muted: '#806000',
		inverse: '#000000',
	},
	accent: {
		primary: '#ffb000',
		focus: '#ffd66b',
		dim: '#a07000',
		glow: 'rgba(255, 176, 0, 0.35)',
	},
	status: {
		success: '#a0d000',
		warning: '#ffb000',
		danger: '#ff6b3a',
		info: '#7bd4ff',
	},
	border: {
		outsetLight: '#806000',
		outsetDark: '#2a2000',
		insetLight: '#2a2000',
		insetDark: '#806000',
		divider: '#4a3a00',
	},
	typography: {
		heading: "'System', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
		body: "'MS Sans Serif', 'System', Tahoma, sans-serif",
		mono: "'Courier New', 'Consolas', monospace",
	},
	effects: {
		focusRing: '0 0 0 1px #ffd66b, 0 0 12px rgba(255, 176, 0, 0.35)',
		tileHover: '0 0 14px rgba(255, 176, 0, 0.25), inset 0 0 0 1px #806000',
	},
};

/**
 * Registry — keep this in alignment with the `PrivacyThemeId`
 * union. The provider validates incoming IDs against this object
 * before persisting, so an unknown ID quietly falls back to the
 * default theme rather than blanking the suite.
 */
export const PRIVACY_THEME_REGISTRY: Readonly<Record<PrivacyThemeId, PrivacyThemeTokens>> = {
	classic: CLASSIC_THEME,
	phosphor: PHOSPHOR_THEME,
};

/**
 * Default theme — used the first time a user opens the suite and
 * when an invalid ID is found in localStorage.
 */
export const DEFAULT_PRIVACY_THEME_ID: PrivacyThemeId = 'classic';

/**
 * Resolve any string to a known theme. Useful when reading
 * persisted values back from disk — we never trust the input.
 */
export function resolvePrivacyThemeId(value: unknown): PrivacyThemeId {
	if (typeof value === 'string' && value in PRIVACY_THEME_REGISTRY) {
		return value as PrivacyThemeId;
	}
	return DEFAULT_PRIVACY_THEME_ID;
}

/**
 * Build the CSS custom-property declarations for a theme.
 *
 * Returns a flat record `{ '--privacy-...': value }`. The provider
 * applies these to `document.documentElement.style` so any CSS
 * inside the suite — including third-party-looking styles in
 * `MoneroPanel.css` once they're refactored — can pick up the
 * current theme by reading the variables.
 *
 * Pure function; no DOM access, so it's trivial to snapshot-test.
 */
export function buildPrivacyThemeVariables(tokens: PrivacyThemeTokens): Record<string, string> {
	return {
		'--privacy-surface-base': tokens.surface.base,
		'--privacy-surface-raised': tokens.surface.raised,
		'--privacy-surface-sunken': tokens.surface.sunken,
		'--privacy-surface-hover': tokens.surface.hover,
		'--privacy-surface-active': tokens.surface.active,
		'--privacy-text-primary': tokens.text.primary,
		'--privacy-text-secondary': tokens.text.secondary,
		'--privacy-text-muted': tokens.text.muted,
		'--privacy-text-inverse': tokens.text.inverse,
		'--privacy-accent-primary': tokens.accent.primary,
		'--privacy-accent-focus': tokens.accent.focus,
		'--privacy-accent-dim': tokens.accent.dim,
		'--privacy-accent-glow': tokens.accent.glow,
		'--privacy-status-success': tokens.status.success,
		'--privacy-status-warning': tokens.status.warning,
		'--privacy-status-danger': tokens.status.danger,
		'--privacy-status-info': tokens.status.info,
		'--privacy-border-outset-light': tokens.border.outsetLight,
		'--privacy-border-outset-dark': tokens.border.outsetDark,
		'--privacy-border-inset-light': tokens.border.insetLight,
		'--privacy-border-inset-dark': tokens.border.insetDark,
		'--privacy-border-divider': tokens.border.divider,
		'--privacy-font-heading': tokens.typography.heading,
		'--privacy-font-body': tokens.typography.body,
		'--privacy-font-mono': tokens.typography.mono,
		'--privacy-effect-focus-ring': tokens.effects.focusRing,
		'--privacy-effect-tile-hover': tokens.effects.tileHover,
	};
}
